import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import AddCropWisePrePost from '../components/AddCropWisePrePost';
import {
    getCropWisePrePostList,
    deleteCropWisePrePost,
    getCrops
} from '../lib/api';

const customStyles = `
  .table-image {
    width: 30px;
    height: 30px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
  }
  
  .content-preview {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const CropWisePrePost = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [activities, setActivities] = useState([]);
    const [activityToEdit, setActivityToEdit] = useState(null);
    const [showEntries, setShowEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [crops, setCrops] = useState([]);
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Get token
    const getToken = () => {
        return localStorage.getItem('authToken') || localStorage.getItem('token');
    };

    const fetchActivities = async () => {
        try {
            setLoading(true);
            const token = getToken();

            // Fetch crops first
            const cropsRes = await getCrops(token);
            const cropsList = cropsRes?.data || cropsRes || [];
            setCrops(cropsList);

            // Fetch activities
            const response = await getCropWisePrePostList(token);
            if (response.status === 200 || response.message) {
                // Get unique crops from the response
                const activitiesData = response.data || [];

                // Transform data to group by crop and get crop name
                const transformedData = await transformActivitiesData(activitiesData, token, cropsList);
                setActivities(transformedData);
            } else {
                console.error('Failed to fetch activities:', response.message);
                setActivities([]);
            }
        } catch (error) {
            console.error('Failed to fetch activities:', error);
            setActivities([]);
        } finally {
            setLoading(false);
        }
    };

    const transformActivitiesData = async (activitiesData, token, cropsList = null) => {
        try {
            // Use provided cropsList or fetch crops
            let cropsForMapping = cropsList;
            if (!cropsForMapping) {
                const cropsRes = await getCrops(token);
                cropsForMapping = cropsRes?.data || cropsRes || [];
            }

            // Create a map of CropID to CropName
            const cropMap = {};
            cropsForMapping.forEach(crop => {
                cropMap[crop.ID] = crop.CropName;
            });

            // Transform the data
            return activitiesData.map(activity => ({
                ID: activity.ID,
                CropID: activity.CropID,
                CropName: cropMap[activity.CropID] || 'Unknown Crop',
                CreatedOn: activity.CreatedOn,
                CreatedBy: activity.CreatedBy,
                LastModifiedOn: activity.LastModifiedOn,
                LastModifiedBy: activity.LastModifiedBy,
                CropWisePrePostSowingDet: activity.CropWisePrePostSowingDet || []
            }));
        } catch (error) {
            console.error('Error transforming data:', error);
            return activitiesData.map(activity => ({
                ...activity,
                CropName: 'Unknown Crop'
            }));
        }
    };

    useEffect(() => {
        fetchActivities();
    }, []);

    const toggleDrawer = (activity = null) => {
        setActivityToEdit(activity);
        setIsDrawerOpen(!isDrawerOpen);
    };

    const handleEdit = (activity) => {
        toggleDrawer(activity);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this activity?')) {
            try {
                const token = getToken();
                await deleteCropWisePrePost(token, id.CropID);
                await fetchActivities();
            } catch (error) {
                console.error("Failed to delete activity:", error);
            }
        }
    };

    // Filter and pagination
    const filteredActivities = activities.filter(activity => {
        const searchMatch = !searchTerm ||
            activity.CropName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            crops.find(c => c.ID === activity.CropID)?.CropName?.toLowerCase().includes(searchTerm.toLowerCase());

        return searchMatch;
    });

    const totalPages = Math.ceil(filteredActivities.length / showEntries);
    const startIndex = (currentPage - 1) * showEntries;
    const endIndex = startIndex + showEntries;
    const currentActivities = filteredActivities.slice(startIndex, endIndex);

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, showEntries]);

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 3;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 2) {
                pages.push(1, 2, 3);
            } else if (currentPage >= totalPages - 1) {
                pages.push(totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(currentPage - 1, currentPage, currentPage + 1);
            }
        }

        return pages;
    };

    return (
        <>
            <style>{customStyles}</style>
            <div className="card p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-nowrap gap-2">
                        <span className="text-sm fw-medium text-secondary-light mb-0">Show</span>
                        <select
                            className="form-select form-select-sm"
                            value={showEntries}
                            onChange={(e) => setShowEntries(parseInt(e.target.value))}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={-1}>All</option>
                        </select>

                        <div className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                name="search"
                                placeholder="Search by crop name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </div>
                    </div>
                    <button
                        onClick={() => toggleDrawer()}
                        className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add New Activity
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-4">Loading activities...</div>
                ) : (
                    <>
                        <div className="table-responsive p-16">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Crop Name</th>
                                        <th scope="col" className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentActivities.map((activity) => (
                                        <tr key={activity.ID}>
                                            <td>
                                                <span className="text-md mb-0 fw-normal text-secondary-light">
                                                    {activity.CropName}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                                                    <button
                                                        type="button"
                                                        className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        onClick={() => handleEdit(activity)}
                                                    >
                                                        <Icon icon="lucide:edit" className="menu-icon" />
                                                    </button>
                                                    {user?.IsDeleteAllowed && (
                                                        <button
                                                            type="button"
                                                            className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            onClick={() => handleDelete(activity)}
                                                        >
                                                            <Icon icon="lucide:trash-2" className="menu-icon" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {filteredActivities.length === 0 && !loading && (
                            <div className="text-center py-4 text-muted">
                                No activities found
                            </div>
                        )}

                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 m-16">
                            <span>
                                Showing {filteredActivities.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, filteredActivities.length)} of {filteredActivities.length} entries
                            </span>
                            {totalPages > 1 && (
                                <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <Icon icon="ep:d-arrow-left" />
                                        </button>
                                    </li>

                                    {getPageNumbers().map(page => (
                                        <li key={page} className="page-item">
                                            <button
                                                className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page ? 'bg-primary-600 text-white' : ''
                                                    }`}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    ))}

                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                        >
                                            <Icon icon="ep:d-arrow-right" />
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </div>

            <AddCropWisePrePost
                isOpen={isDrawerOpen}
                onClose={() => {
                    setActivityToEdit(null);
                    setIsDrawerOpen(false);
                }}
                onSaveSuccess={fetchActivities}
                activityToEdit={activityToEdit}
            />
        </>
    );
};

export default CropWisePrePost;